import {
  AppBar,
  Avatar,
  Box,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import BoltIcon from '@mui/icons-material/Bolt';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LinkIcon from '@mui/icons-material/Link';
import { useContext } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { ActiveSubscriptionContext } from './active-subscription-context';
import { UserPill } from './user-pill.component';

const NO_COMPANY_TOOLTIP = 'Elegí una empresa primero';
const NO_SUBSCRIPTION_TOOLTIP = 'Activá la suscripción de la empresa primero';

interface ParsedPath {
  companyId: string | null;
  activeTab: string;
}

/**
 * Parses the current pathname into the company id (if any) and the
 * active tab. The tab value matches the URL segment after the company
 * id so `<Tabs value={…}>` stays in sync with the route.
 */
function parsePath(pathname: string): ParsedPath {
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] !== 'company' || !segments[1]) {
    return { companyId: null, activeTab: 'company' };
  }
  const companyId = segments[1];
  const tab = segments[2] ?? 'company';

  return { companyId, activeTab: tab };
}

function buildTabHref(companyId: string, tabValue: string): string {
  if (tabValue === 'company') {
    return `/company/${companyId}`;
  }
  return `/company/${companyId}/${tabValue}`;
}

interface AppShellTab {
  value: string;
  label: string;
  icon: JSX.Element;
  // Billing tabs need an active subscription, not just a company in the URL:
  // a company that exists in Kori without a subscription lands on "Activar
  // suscripción" and cannot contract a plan/package/credit/line until then.
  requiresSubscription: boolean;
  disabledTooltip?: string;
}

const TABS: AppShellTab[] = [
  {
    value: 'company',
    label: 'Empresa',
    icon: <BusinessIcon fontSize="small" />,
    requiresSubscription: false,
  },
  {
    value: 'channel-onboarding',
    label: 'Alta de línea',
    icon: <LinkIcon fontSize="small" />,
    requiresSubscription: true,
  },
  {
    value: 'plan',
    label: 'Plan',
    icon: <BoltIcon fontSize="small" />,
    requiresSubscription: true,
  },
  {
    value: 'add-ons',
    label: 'Paquete',
    icon: <Inventory2OutlinedIcon fontSize="small" />,
    requiresSubscription: true,
  },
  {
    value: 'credit-line',
    label: 'Crédito',
    icon: <CreditCardIcon fontSize="small" />,
    requiresSubscription: true,
  },
];

/**
 * Global app chrome. Wraps authenticated routes with:
 * - Sticky top bar: HSM Admin brand on the left, centred tabs and the
 *   `UserPill` on the right. The Alta de línea / Plan / Paquete / Crédito
 *   tabs require an active subscription (read from `ActiveSubscriptionContext`,
 *   which `CompanyShell` populates) — they stay disabled with a tooltip on
 *   the search screen and while a company has no subscription.
 * - Body that hosts an `<Outlet />` so child routes render underneath.
 */
export const AppShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasActiveSubscription } = useContext(ActiveSubscriptionContext);
  const { companyId, activeTab } = parsePath(location.pathname);

  const handleNavigate = (tabValue: string) => {
    if (!companyId) {
      return;
    }
    navigate(buildTabHref(companyId, tabValue));
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Toolbar sx={{ gap: 3 }}>
          <Brand onClick={() => navigate('/')} />
          <Tabs
            value={activeTab}
            textColor="primary"
            indicatorColor="primary"
            sx={{ flexGrow: 1, minHeight: 64 }}
          >
            {TABS.map((tab) => (
              <TabWithTooltip
                key={tab.value}
                tab={tab}
                hasCompany={Boolean(companyId)}
                hasActiveSubscription={hasActiveSubscription}
                onNavigate={handleNavigate}
              />
            ))}
          </Tabs>
          <UserPill />
        </Toolbar>
      </AppBar>
      <Box component="main">
        <Outlet />
      </Box>
    </Box>
  );
};

/** Brand block (logo + name). Clicking it returns to the company search ("/"),
 * the conventional "home" target. */
const Brand = ({ onClick }: { onClick: () => void }) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="center"
    onClick={onClick}
    role="button"
    aria-label="Volver a buscar empresa"
    sx={{ cursor: 'pointer' }}
  >
    <Avatar
      variant="rounded"
      sx={{
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        width: 36,
        height: 36,
        fontWeight: 700,
        fontSize: 14,
      }}
    >
      HA
    </Avatar>
    <Box>
      <Typography variant="body1" fontWeight={700} lineHeight={1.2}>
        HSM Admin
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Gestión de líneas HSM
      </Typography>
    </Box>
  </Stack>
);

interface TabWithTooltipProps {
  tab: AppShellTab;
  hasCompany: boolean;
  hasActiveSubscription: boolean;
  onNavigate: (tabValue: string) => void;
}

const TabWithTooltip = ({
  tab,
  hasCompany,
  hasActiveSubscription,
  onNavigate,
}: TabWithTooltipProps) => {
  const disabledByFlag = Boolean(tab.disabledTooltip);
  // Two gates for the billing tabs: first a company in the URL, then an
  // active subscription for it. The tooltip explains whichever is missing.
  const disabledByCompany = tab.requiresSubscription && !hasCompany;
  const disabledBySubscription =
    tab.requiresSubscription && hasCompany && !hasActiveSubscription;
  const disabled =
    disabledByFlag || disabledByCompany || disabledBySubscription;
  const tooltip = tab.disabledTooltip
    ? tab.disabledTooltip
    : disabledByCompany
      ? NO_COMPANY_TOOLTIP
      : disabledBySubscription
        ? NO_SUBSCRIPTION_TOOLTIP
        : undefined;

  // Bindeo la navegación al click del Tab (no a onChange de Tabs) porque
  // cuando el Tab está wrappeado por TabWithTooltip MUI no propaga el
  // evento al onChange del padre.
  const inner = (
    <Tab
      value={tab.value}
      label={tab.label}
      icon={tab.icon}
      iconPosition="start"
      disabled={disabled}
      onClick={() => onNavigate(tab.value)}
      sx={{ minHeight: 64, textTransform: 'none', fontWeight: 600 }}
    />
  );

  if (!tooltip) {
    return inner;
  }

  // Tooltip no envuelve elementos disabled — usamos un span wrapper.
  return (
    <Tooltip title={tooltip} placement="bottom">
      <span>{inner}</span>
    </Tooltip>
  );
};
